import React, { useMemo } from 'react'
import { Chip, Stack, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectOrdersHistory } from '../../selectors/orders'
import AiCard, { AiCardDivider } from './cardKit/AiCard'
import OrderStatusDisplay from '../orderStatus/orderStatusDisplay'
import OrderTrackerActions from './OrderTrackerActions'
import { trackerActionsForOrder } from './trackerActions'
import {
    formatInquiryLegPrice,
    inquiryLegTitle,
    inquiryStatusToOrderStatus,
    resolveOrderInquiryWithOms,
    toLinkedOmsOrder,
    type OrderInquiry,
    type OrderInquiryGroup,
    type OrderInquiryLeg,
} from './domains/tradingDomain'

type Props = {
    inquiry: OrderInquiry
}

function legActions(leg: OrderInquiryLeg, isBracket: boolean) {
    const roleLabel = !isBracket
        ? undefined
        : leg.role === 'PROTECTIVE_STOP'
          ? 'stop'
          : leg.role === 'TARGET'
            ? 'target'
            : undefined
    return trackerActionsForOrder({
        orderId: leg.orderId,
        status: inquiryStatusToOrderStatus(leg.status),
        orderType: leg.orderType,
        bracketEntry: isBracket && leg.role === 'ENTRY',
        roleLabel,
    })
}

function LegLine({ leg }: { leg: OrderInquiryLeg }) {
    const priceLabel = formatInquiryLegPrice(leg)
    const omsStatus = inquiryStatusToOrderStatus(leg.status)
    return (
        <>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
                {inquiryLegTitle(leg.role)}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                <Typography variant="body2" fontWeight={600} className="font-feature-num">
                    {leg.orderType}
                    {priceLabel ? ` · ${priceLabel}` : ''}
                </Typography>
                {omsStatus != null ? (
                    <Typography variant="caption" component="span">
                        <OrderStatusDisplay status={omsStatus} />
                    </Typography>
                ) : (
                    <Chip size="small" label={leg.status.replace(/^ORDER_STATUS_/, '')} variant="outlined" sx={{ height: 20 }} />
                )}
            </Stack>
        </Stack>
        <OrderTrackerActions actions={legActions(leg, true)} />
        </>
    )
}

function InquiryGroupCard({ group }: { group: OrderInquiryGroup }) {
    const entry = group.legs.find((leg) => leg.role === 'ENTRY') ?? group.legs[0]
    const isBracket = group.kind === 'BRACKET' || group.legs.length > 1
    const entryPrice = formatInquiryLegPrice(entry)
    const sideColor = entry.side.toUpperCase() === 'BUY' ? 'buy.main' : 'sell.main'
    const headerType = isBracket ? 'Bracket' : entry.orderType
    const omsStatus = !isBracket ? inquiryStatusToOrderStatus(entry.status) : undefined

    const oneLiner = !isBracket
        ? `${entry.side.toUpperCase()} ${entry.quantity}${entryPrice ? ` · ${entryPrice}` : ''}`
        : `${entry.side.toUpperCase()} · ${headerType}`

    return (
        <AiCard accent={entry.side.toUpperCase() === 'BUY' ? 'buy' : 'sell'}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="subtitle2" fontWeight={700}>
                    {group.instrument.label}
                </Typography>
                <Chip size="small" label={group.exchange} variant="outlined" sx={{ height: 20 }} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.75 }}>
                <Typography variant="body2" fontWeight={600} color={sideColor} className="font-feature-num">
                    {oneLiner}
                </Typography>
                {!isBracket && omsStatus != null ? (
                    <Typography variant="caption" component="span">
                        <OrderStatusDisplay status={omsStatus} />
                    </Typography>
                ) : null}
            </Stack>
            {!isBracket ? <OrderTrackerActions actions={legActions(entry, false)} /> : null}
            {isBracket ? (
                <>
                    <AiCardDivider />
                    {group.legs.map((leg) => (
                        <LegLine key={`${leg.clientOrderId}-${leg.role}`} leg={leg} />
                    ))}
                </>
            ) : null}
        </AiCard>
    )
}

export default function OrderInquiryCard({ inquiry: initialInquiry }: Props) {
    const ordersHistory = useSelector(selectOrdersHistory)
    const linkedOmsOrders = useMemo(() => ordersHistory.map(toLinkedOmsOrder), [ordersHistory])
    const inquiry = useMemo(
        () => resolveOrderInquiryWithOms(initialInquiry, linkedOmsOrders),
        [initialInquiry, linkedOmsOrders],
    )

    if (inquiry.outcome === 'NO_MATCH') {
        return null
    }

    if (inquiry.outcome === 'AMBIGUOUS' && inquiry.matchSummaries?.length) {
        return (
            <Stack spacing={0}>
                {inquiry.matchSummaries.map((summary) => (
                    <AiCard key={summary.orderId || `${summary.side}-${summary.quantity}-${summary.price}`} accent="neutral">
                        <Typography variant="subtitle2" fontWeight={700}>
                            {summary.instrumentLabel}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} className="font-feature-num">
                            {summary.side} · {summary.orderType}
                            {summary.price ? ` · ₹${summary.price}` : ''} · {summary.status}
                        </Typography>
                    </AiCard>
                ))}
            </Stack>
        )
    }

    const groups = inquiry.groups.slice(0, 5)
    const hidden = inquiry.groups.length - groups.length

    return (
        <Stack spacing={0}>
            {groups.map((group, index) => (
                <InquiryGroupCard key={`${group.basketId || group.instrument.code}-${index}`} group={group} />
            ))}
            {hidden > 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    +{hidden} more order{hidden === 1 ? '' : 's'}
                </Typography>
            ) : null}
        </Stack>
    )
}
